import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ArticleLayoutProps {
  title: string;
  excerpt: string;
  image: string;
  date: string;
  readTime: string;
  slug: string;
  children: React.ReactNode;
}

export default function ArticleLayout({ title, excerpt, image, date, readTime, slug, children }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articleUrl = `https://vigorbuyspread.cyou/${slug}`;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": excerpt,
    "image": image,
    "datePublished": "2026-07-23",
    "dateModified": "2026-07-23",
    "author": {
      "@type": "Organization",
      "name": "VigorBuy Spreadsheet",
      "url": "https://vigorbuyspread.cyou"
    },
    "publisher": {
      "@type": "Organization",
      "name": "VigorBuy Spreadsheet",
      "url": "https://vigorbuyspread.cyou"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    },
    "url": articleUrl
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <article className="w-full px-6 md:px-10 max-w-4xl mx-auto py-10 md:py-14">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              Home
            </Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <span className="text-foreground-800 font-medium">{title}</span>
          </nav>

          <header className="mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
              SEO Article
            </span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
              {title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">
              {excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </span>
                {date}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {readTime}
              </span>
            </div>
          </header>

          <div className="relative w-full aspect-[21/9] rounded-2xl overflow-hidden mb-10">
            <img
              src={image}
              alt={title}
              className="w-full h-full object-cover rounded-2xl"
            />
          </div>

          <div className="prose-custom">
            {children}

            <section className="mt-12 p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-3">
                Ready to Transform Your Spreadsheet Workflow?
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-5">
                Discover professional VigorBuy Spreadsheet templates, business tools, and productivity solutions designed to simplify data management and improve business efficiency.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Explore VigorBuy Spreadsheet
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full border-2 border-primary-300 text-primary-700 hover:bg-primary-100 transition-all whitespace-nowrap"
                >
                  Back to Homepage
                </Link>
              </div>
            </section>

            <nav className="border-t border-background-200 pt-8 mt-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </span>
                  Return to Homepage
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Visit Official Store
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line"></i>
                  </span>
                </a>
              </div>
            </nav>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}